import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import {
  ArrowLeft, BookOpen, CheckCircle2, Circle, Play, Square, Trash2,
  Plus, Clock, Users, EyeOff, Eye, UserCircle2,
} from 'lucide-react';

interface ManualHW {
  id: string;
  title: string;
  description: string | null;
  subject_code: string | null;
  due_at: string | null;
  shared_class: string | null;
  created_by: string;
  created_by_name: string;
  is_mine: boolean;
  started_at: string | null;
  completed_at: string | null;
  total_work_seconds: number;
  hidden_at: string | null;
  active_session_started_at: string | null;
}

interface Subject { code: string; display_name: string | null; }

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function Timer({ start }: { start: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const sec = Math.max(0, Math.floor((now - new Date(start).getTime()) / 1000));
  return <span className="text-yellow-300 font-mono text-xs">{formatDuration(sec)}</span>;
}

const ManuelleHAPage: React.FC = () => {
  const [items, setItems] = useState<ManualHW[]>([]);
  const [userClass, setUserClass] = useState<string | null>(null);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [includeDone, setIncludeDone] = useState(false);
  const [includeHidden, setIncludeHidden] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newDueAt, setNewDueAt] = useState('');
  const [newShare, setNewShare] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (includeDone) params.set('include_done', '1');
      if (includeHidden) params.set('include_hidden', '1');
      const [r, s] = await Promise.all([
        api<{ homework: ManualHW[]; user_class: string | null }>(`/api/manual-homework?${params.toString()}`),
        api<{ subjects: Subject[] }>('/api/subjects').catch(() => ({ subjects: [] as Subject[] })),
      ]);
      setItems(r.homework);
      setUserClass(r.user_class);
      setSubjects(s.subjects);
    } catch (err: any) {
      setError(err?.message || 'Fehler beim Laden');
    }
  }, [includeDone, includeHidden]);

  useEffect(() => { load(); }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setBusy('create'); setError(null);
    try {
      await api('/api/manual-homework', {
        method: 'POST',
        body: JSON.stringify({
          title: newTitle,
          description: newDescription || null,
          subject_code: newSubject || null,
          due_at: newDueAt || null,
          share_with_my_class: newShare,
        }),
      });
      setNewTitle(''); setNewDescription(''); setNewSubject(''); setNewDueAt(''); setNewShare(false);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Erstellen fehlgeschlagen');
    } finally { setBusy(null); }
  };

  const toggleComplete = async (h: ManualHW) => {
    setBusy(h.id);
    try {
      await api(`/api/manual-homework/${h.id}/complete`, {
        method: 'POST',
        body: JSON.stringify({ completed: !h.completed_at }),
      });
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const toggleTimer = async (h: ManualHW) => {
    setBusy(h.id);
    try {
      await api(`/api/manual-homework/${h.id}/${h.active_session_started_at ? 'stop' : 'start'}`, { method: 'POST' });
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const remove = async (h: ManualHW) => {
    if (!window.confirm(`„${h.title}" wirklich löschen? (betrifft alle, denen diese HA geteilt wurde)`)) return;
    setBusy(h.id);
    try {
      await api(`/api/manual-homework/${h.id}`, { method: 'DELETE' });
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const hide = async (h: ManualHW, hidden: boolean) => {
    setBusy(h.id);
    try {
      await api(`/api/manual-homework/${h.id}/hide`, {
        method: 'POST',
        body: JSON.stringify({ hidden }),
      });
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const sorted = useMemo(() => items, [items]);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
        <Link to="/dashboard" className="text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <BookOpen className="w-6 h-6 text-indigo-400" />
        <h1 className="text-xl font-semibold">Manuelle Hausaufgaben</h1>
        <div className="ml-auto flex gap-4 text-sm text-gray-400 flex-wrap">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={includeDone} onChange={e => setIncludeDone(e.target.checked)} className="accent-blue-500" />
            Erledigte
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={includeHidden} onChange={e => setIncludeHidden(e.target.checked)} className="accent-blue-500" />
            Ausgeblendete
          </label>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
        <form onSubmit={create} className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
          <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)}
            placeholder="z.B. Mathe S. 47 Nr. 3"
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg placeholder-gray-400 focus:outline-none focus:border-blue-500" />
          <textarea value={newDescription} onChange={e => setNewDescription(e.target.value)}
            placeholder="Details (optional)"
            rows={2}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg placeholder-gray-400 text-sm focus:outline-none focus:border-blue-500" />
          <div className="flex flex-wrap gap-3 items-center">
            <select value={newSubject} onChange={e => setNewSubject(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-200 text-sm">
              <option value="">Fach (optional)</option>
              {subjects.map(s => <option key={s.code} value={s.code}>{s.display_name ?? s.code}</option>)}
            </select>
            <input type="datetime-local" value={newDueAt} onChange={e => setNewDueAt(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-200 text-sm" />
            <label className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border cursor-pointer transition ${newShare ? 'border-indigo-500 bg-indigo-500/10 text-indigo-200' : 'border-gray-600 bg-gray-700 text-gray-400 hover:text-gray-200'}`}>
              <input type="checkbox" checked={newShare} onChange={e => setNewShare(e.target.checked)} className="accent-indigo-500" />
              <Users className="w-4 h-4" />
              Mit Klasse {userClass ? <code className="bg-gray-800 px-1 rounded">{userClass}</code> : '(nicht hinterlegt)'} teilen
            </label>
            <button type="submit" disabled={busy === 'create' || !newTitle.trim()}
              className="ml-auto bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2">
              <Plus className="w-4 h-4" /> Anlegen
            </button>
          </div>
        </form>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <ul className="space-y-3">
          {sorted.length === 0 ? (
            <p className="text-gray-500 text-sm">Noch keine manuellen Hausaufgaben. Lege oben eine an — optional für die ganze Klasse.</p>
          ) : sorted.map(h => (
            <li key={h.id} className={`bg-gray-800 border ${h.hidden_at ? 'border-gray-700 opacity-50' : 'border-gray-700'} rounded-lg p-4 flex items-start gap-3 ${h.completed_at ? 'opacity-60' : ''}`}>
              <button onClick={() => toggleComplete(h)} disabled={busy === h.id} className="mt-1">
                {h.completed_at
                  ? <CheckCircle2 className="w-5 h-5 text-green-400" />
                  : <Circle className="w-5 h-5 text-gray-500 hover:text-gray-300" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`font-medium flex items-center gap-2 flex-wrap ${h.completed_at ? 'line-through text-gray-400' : ''}`}>
                  {h.title}
                  {h.shared_class && (
                    <span className="bg-indigo-500/20 text-indigo-200 text-xs px-2 py-0.5 rounded flex items-center gap-1">
                      <Users className="w-3 h-3" /> Klasse {h.shared_class}
                    </span>
                  )}
                  {!h.is_mine && (
                    <span className="bg-gray-700 text-gray-300 text-xs px-2 py-0.5 rounded flex items-center gap-1">
                      <UserCircle2 className="w-3 h-3" /> von {h.created_by_name}
                    </span>
                  )}
                </div>
                {h.description && <div className="text-sm text-gray-400 mt-1 whitespace-pre-wrap">{h.description}</div>}
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-400">
                  {h.due_at && <span>📅 {new Date(h.due_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</span>}
                  {h.subject_code && <span className="bg-gray-700 px-1.5 py-0.5 rounded">{h.subject_code}</span>}
                  {h.total_work_seconds > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(h.total_work_seconds)}</span>}
                  {h.active_session_started_at && <Timer start={h.active_session_started_at} />}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!h.completed_at && (
                  <button onClick={() => toggleTimer(h)} disabled={busy === h.id}
                    className={`p-2 rounded-lg transition ${h.active_session_started_at ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-gray-700 hover:bg-gray-600'}`}
                    title={h.active_session_started_at ? 'Timer stoppen' : 'Timer starten'}>
                    {h.active_session_started_at ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                )}
                {h.is_mine ? (
                  <button onClick={() => remove(h)} disabled={busy === h.id}
                    className="p-2 rounded-lg bg-gray-700 hover:bg-red-600 transition" title="Löschen">
                    <Trash2 className="w-4 h-4" />
                  </button>
                ) : (
                  <button onClick={() => hide(h, !h.hidden_at)} disabled={busy === h.id}
                    className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition"
                    title={h.hidden_at ? 'Wieder einblenden' : 'Für mich ausblenden'}>
                    {h.hidden_at ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
};

export default ManuelleHAPage;
